/**
 * ==============================================================================
 * BUSINESS LAYER (BL) - DÍA DE PICNIC
 * Valida reglas de negocio y formatea los datos antes de enviarlos a la API.
 * ==============================================================================
 */

import { PrepareScreenForPicnicApiCall as BLPrepareScreenForPicnicApiCall, StartPicnicMatchApiCall as BLStartPicnicMatchApiCall, StopPicnicMatchApiCall as BLStopPicnicMatchApiCall, SendPicnicScoreProgressApiCall as BLSendPicnicScoreProgressApiCall } from './../DL/Picnic'


const PreparePicnicDayScreen = async () => {
    try {
        const response = await BLPrepareScreenForPicnicApiCall();
        return response;
    } catch (error) {
        return { success: false, message: "Excepción en la lógica de negocio al preparar pantalla." };
    }
};

const StartPicnicDayMatch = async (matchConfigurationObject) => {
    try {
        // 1. Validaciones de Negocio
        if (!matchConfigurationObject) {
            return { success: false, message: "La configuración del juego está vacía." };
        }
        if (matchConfigurationObject.roundDurationSeconds < 10) {
            return { success: false, message: "La partida debe durar al menos 10 segundos." };
        }

        // 2. Preparamos los datos aislando la duración y convirtiendo el resto a JSON string
        const durationForBackend = parseInt(matchConfigurationObject.roundDurationSeconds);
        const settingsAsJsonString = JSON.stringify(matchConfigurationObject);

        // 3. Ejecutar petición en Capa de Datos (DL)
        const response = await BLStartPicnicMatchApiCall(durationForBackend, settingsAsJsonString);
        return response;

    } catch (error) {
        return { success: false, message: "Excepción en la lógica de negocio al iniciar partida." };
    }
};

const StopPicnicDayMatch = async (currentMatchIdentifier) => {
    try {
        // 1. Validación
        if (!currentMatchIdentifier) {
            return { success: false, message: "No hay una partida activa para detener." };
        }

        // 2. Ejecutar
        const response = await BLStopPicnicMatchApiCall(currentMatchIdentifier);
        return response;
    } catch (error) {
        return { success: false, message: "Excepción en la lógica de negocio al detener partida." };
    }
};

/**
 * ==============================================================================
 * BUSINESS LAYER (BL) - PICNIC FAN
 * Gestiona el WebSocket dedicado a la dinámica Día de Picnic.
 * Incluye lógica de reconexión idéntica a RunToGoal.
 * ==============================================================================
 */

// 🔌 WebSocket (dedicado a Picnic)
let picnicSocket = null;
let picnicIsConnected = false;
let picnicReconnectTimeout = null;
let picnicReconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Conecta el dispositivo al WebSocket como un jugador específico (lucho o tunita).
 */
const ConnectPicnicWebSocket = async (playerId, onStatus = () => {}, onToast = () => {}) => {
    if (picnicSocket || picnicIsConnected) return;

    const connect = () => {
        if (picnicReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            onStatus('error'); 
            return;
        }
        
        picnicReconnectAttempts++;
        
        // URL idéntica a la que usas en Goal, pero con tipo=picnic:{playerId}
        picnicSocket = new WebSocket(`${process.env.REACT_APP_API_URL_WSS}?tipo=picnic:${playerId}`);
        onStatus('connecting');

        picnicSocket.onopen = () => {
            picnicIsConnected = true;
            picnicReconnectAttempts = 0;
            onStatus('connected');
            onToast('¡Conectado!');
        };

        picnicSocket.onclose = () => {
            picnicIsConnected = false;
            picnicSocket = null;
            onStatus('reconnecting');
            picnicReconnectTimeout = setTimeout(connect, 2000);
        };

        picnicSocket.onerror = () => onStatus('error');
    };
    connect();
};

/**
 * Asigna la función que se ejecutará cuando llegue un mensaje del servidor.
 */
const SetPicnicSocketMessageHandler = (handler) => {
    if (!picnicSocket) return;
    picnicSocket.onmessage = (evt) => {
        try { 
            handler(JSON.parse(evt.data)); 
        } catch (error) {
            console.error("Error parseando mensaje Picnic:", error);
        }
    };
};

/**
 * Limpia el handler de mensajes.
 */
const ClearPicnicSocketMessageHandler = () => {
    if (picnicSocket) picnicSocket.onmessage = null;
};

/**
 * Desconecta y limpia totalmente el WebSocket.
 */
const DisconnectPicnicWebSocket = () => {
    try {
        if (picnicSocket) {
            picnicSocket.onopen = picnicSocket.onclose = picnicSocket.onerror = picnicSocket.onmessage = null;
            picnicSocket.close();
            picnicSocket = null;
            picnicIsConnected = false;
            clearTimeout(picnicReconnectTimeout);
            picnicReconnectAttempts = 0;
        }
    } catch (error) {}
};

/**
 * 📡 REST (DL Inyectado aquí mismo para simplificar, como en Goal.js)
 * Envía el progreso de puntos actuales al servidor de forma silenciosa.
 */
// export const SendPicnicScoreProgress = async ({ matchId, playerId, currentScore }) => {
//     try {
//         const res = await fetch(`${process.env.REACT_APP_API_URL}admin/picnic/progress`, {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ matchId, playerId, currentScore })
//         });
//         return { success: res.ok };
//     } catch {
//         return { success: false }; // Falla en silencio para no trabar el juego
//     }
// };

// Wrapper para enviar puntos
// const SendPicnicScoreProgress = async (matchId, playerId, score) => {
//     if (!matchId || !playerId) return false;
//     return await BLSendPicnicScoreProgressApiCall(matchId, playerId, score);
// };
    
// Wrapper para enviar puntos
const SendPicnicScoreProgress = async ({ matchId, playerId, currentScore }) => {
    if (!matchId || !playerId) return false;
    return await BLSendPicnicScoreProgressApiCall(matchId, playerId, currentScore);
};

export { PreparePicnicDayScreen, StartPicnicDayMatch, StopPicnicDayMatch, ConnectPicnicWebSocket, SetPicnicSocketMessageHandler, ClearPicnicSocketMessageHandler, DisconnectPicnicWebSocket, SendPicnicScoreProgress };